import { useEffect, useState } from "react";
import { Navigate } from "react-router-dom";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from "recharts";
import { ShoppingCart, Banknote, Boxes, Package, Truck, AlertTriangle } from "lucide-react";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { fmtMoney, fmtDate, statusLabel, statusClass, unitLabel } from "../lib/format";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";

const Stat = ({ icon: Icon, label, value, sub, testId, delay }) => (
  <div className="fade-up border border-stone-200 bg-white p-5" style={{ animationDelay: `${delay}ms` }} data-testid={testId}>
    <div className="flex items-center justify-between">
      <p className="text-xs uppercase tracking-[0.2em] text-stone-500">{label}</p>
      <Icon size={16} className="text-[#1C4E35]" strokeWidth={1.8} />
    </div>
    <p className="font-heading mt-3 text-3xl font-black tracking-tight text-[#0A0D0B]">{value}</p>
    {sub && <p className="mt-1 text-xs text-stone-500">{sub}</p>}
  </div>
);

const DASH_STATUS = [
  ["PENDING", "Planlanan"],
  ["PAYING", "Ödeme Başlatıldı"],
  ["PAID", "Ödenen"],
  ["CANCELLED", "İptal Edilen"],
];

const LateDeliveriesPanel = () => {
  const [items, setItems] = useState(null);
  useEffect(() => {
    api.get("/admin/late-deliveries").then((r) => setItems(r.data.data)).catch(() => {});
  }, []);
  if (!items) return null;
  return (
    <div className="fade-up border border-stone-200 bg-white p-5" style={{ animationDelay: "300ms" }} data-testid="late-deliveries-panel">
      <p className="flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-stone-500">
        <AlertTriangle size={14} className={items.length ? "text-red-600" : "text-green-600"} /> Geciken Teslimatlar
      </p>
      {items.length === 0 ? (
        <p className="mt-3 text-sm text-green-700" data-testid="late-deliveries-empty">Geciken teslimat yok ✓</p>
      ) : (
        <div className="mt-3 space-y-1.5">
          {items.map((x, i) => (
            <div key={i} className="flex flex-wrap items-center gap-x-3 gap-y-1 border border-red-200 bg-red-50 px-3 py-2 text-xs" data-testid={`late-delivery-${i}`}>
              <span className="font-medium text-red-800">{x.name || x.sku}{x.variant ? ` — ${x.variant}` : ""}</span>
              <span className="text-red-600">Hizmet başlangıcı: {x.firstDate}</span>
              <span className="text-stone-600">Teslim: {x.delivered}/{x.assigned}</span>
              <span className="font-heading font-bold text-red-700">Kalan: {x.remaining}</span>
              {x.suppliers?.length > 0 && <span className="text-stone-500">{x.suppliers.join(", ")}</span>}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

const SupplierProgressPanel = () => {
  const [data, setData] = useState(null);
  useEffect(() => {
    api.get("/admin/supplier-progress").then((r) => setData(r.data.data)).catch(() => {});
  }, []);
  if (!data || data.length === 0) return null;
  return (
    <div className="fade-up border border-stone-200 bg-white p-5" style={{ animationDelay: "260ms" }} data-testid="supplier-progress-panel">
      <p className="flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-stone-500">
        <Truck size={14} /> Tedarikçi Teslimat İlerlemesi
      </p>
      <div className="mt-4 grid grid-cols-1 gap-x-8 gap-y-4 md:grid-cols-2">
        {data.map((s) => {
          const pct = Math.min(100, (s.deliveredQty / (s.assignedQty || 1)) * 100);
          const done = s.deliveredQty >= s.assignedQty && s.assignedQty > 0;
          return (
            <div key={s.supplierId} data-testid={`supplier-progress-${s.supplierId}`}>
              <div className="flex items-baseline justify-between text-sm">
                <span className="truncate font-medium text-stone-800">{s.supplier}</span>
                <span className="ml-2 shrink-0 text-xs text-stone-500">
                  <b className={`font-heading text-sm ${done ? "text-green-700" : "text-[#1C4E35]"}`}>{s.deliveredQty.toLocaleString("tr-TR")}</b>
                  {" / "}{s.assignedQty.toLocaleString("tr-TR")} adet · {s.productCount} ürün · %{Math.round(pct)}
                </span>
              </div>
              <div className="mt-1.5 h-1.5 w-full bg-stone-200">
                <div className={`h-1.5 transition-all duration-700 ${done ? "bg-green-600" : "bg-[#1C4E35]"}`} style={{ width: `${pct}%` }} />
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

const StatusKpiRow = () => {
  const [data, setData] = useState(null);
  useEffect(() => {
    api.get("/orders/status-summary").then((r) => setData(r.data)).catch(() => {});
  }, []);
  if (!data) return null;
  const by = Object.fromEntries(data.data.map((x) => [x.status, x]));
  const allCount = data.data.filter((x) => x.status !== "CANCELLED").reduce((a, x) => a + x.count, 0);
  const allTotals = {};
  data.data.forEach((x) => {
    if (x.status === "CANCELLED") return;
    Object.entries(x.totals).forEach(([c, v]) => { allTotals[c] = (allTotals[c] || 0) + v; });
  });
  const money = (t) => Object.entries(t || {}).map(([c, v]) => fmtMoney(v, c)).join(" + ") || "-";
  const cards = [
    { key: "ALL", label: "Tümü", cls: "border-stone-400 bg-stone-100 text-stone-700", count: allCount, totals: allTotals, sub: "İptaller hariç" },
    ...DASH_STATUS.map(([k, label]) => ({ key: k, label, cls: statusClass(k), count: by[k]?.count || 0, totals: by[k]?.totals })),
  ];
  return (
    <div className="grid grid-cols-2 gap-4 md:grid-cols-3 lg:grid-cols-5" data-testid="dashboard-status-kpis">
      {cards.map((c, i) => (
        <div key={c.key} className="fade-up border border-stone-200 bg-white p-4" style={{ animationDelay: `${220 + i * 40}ms` }} data-testid={`dash-status-kpi-${c.key}`}>
          <div className="flex items-center justify-between">
            <span className={`border px-2 py-0.5 text-[10px] font-medium ${c.cls}`}>{c.label}</span>
            {c.sub && <span className="text-[9px] uppercase tracking-wider text-stone-400">{c.sub}</span>}
          </div>
          <p className="font-heading mt-2 text-2xl font-black tracking-tight text-[#0A0D0B]">
            {c.count} <span className="text-sm font-medium text-stone-500">sipariş</span>
          </p>
          <p className="mt-0.5 text-xs text-stone-500" data-testid={`dash-status-kpi-${c.key}-total`}>{money(c.totals)}</p>
        </div>
      ))}
    </div>
  );
};

export default function DashboardPage() {
  const { user } = useAuth();
  const [stats, setStats] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    api
      .get("/dashboard/stats")
      .then((r) => setStats(r.data))
      .catch((e) => setError(e.response?.data?.detail || "Veriler yüklenemedi"));
  }, []);

  if (user?.role === "supplier") return <Navigate to="/tedarik-siparisleri" replace />;
  if (error)
    return <p className="border border-red-300 bg-red-50 p-4 text-sm text-red-700" data-testid="dashboard-error">{error}</p>;
  if (!stats)
    return (
      <div className="flex h-64 items-center justify-center" data-testid="dashboard-loading">
        <div className="h-6 w-6 animate-spin rounded-full border-2 border-[#1C4E35] border-t-transparent" />
      </div>
    );

  const moneyStr = Object.entries(stats.totalsByCurrency)
    .map(([c, v]) => fmtMoney(v, c))
    .join(" + ") || "-";
  const chartData = stats.topProducts.slice(0, 8).map((p) => ({ name: p.name?.slice(0, 22), adet: p.qty }));

  return (
    <div className="space-y-6" data-testid="dashboard-page">
      <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-4">
        <Stat icon={ShoppingCart} label="Toplam Sipariş" value={stats.totalOrders} testId="stat-total-orders" delay={0} />
        <Stat icon={Banknote} label="Toplam Tutar" value={moneyStr} sub="KDV dahil" testId="stat-total-amount" delay={60} />
        <Stat icon={Boxes} label="Toplam Adet" value={stats.totalQty.toLocaleString("tr-TR")} sub="Tüm sipariş satırları" testId="stat-total-qty" delay={120} />
        <Stat icon={Package} label="Sipariş Edilen Ürün" value={stats.uniqueProducts} sub="Farklı ürün çeşidi" testId="stat-unique-products" delay={180} />
      </div>

      <StatusKpiRow />

      {user?.role === "admin" && <LateDeliveriesPanel />}

      {user?.role === "admin" && <SupplierProgressPanel />}

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
        <div className="fade-up border border-stone-200 bg-white p-5 lg:col-span-2" style={{ animationDelay: "240ms" }}>
          <p className="text-xs uppercase tracking-[0.2em] text-stone-500">En Çok Sipariş Edilen Ürünler (Adet)</p>
          <div className="mt-4 h-72">
            <ResponsiveContainer width="100%" height="100%" minHeight={280}>
              <BarChart data={chartData} margin={{ left: -10, right: 10 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#E5E5E3" vertical={false} />
                <XAxis dataKey="name" tick={{ fontSize: 10, fill: "#575E59" }} interval={0} angle={-20} textAnchor="end" height={60} />
                <YAxis tick={{ fontSize: 11, fill: "#575E59" }} />
                <Tooltip cursor={{ fill: "#F7F7F6" }} contentStyle={{ borderRadius: 0, border: "1px solid #E5E5E3", fontSize: 12 }} />
                <Bar dataKey="adet" fill="#1C4E35" radius={[2, 2, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="fade-up border border-stone-200 bg-white p-5" style={{ animationDelay: "300ms" }}>
          <p className="text-xs uppercase tracking-[0.2em] text-stone-500">Sipariş Durumları</p>
          <div className="mt-4 space-y-3" data-testid="status-breakdown">
            {stats.statusCounts.map((s) => (
              <div key={s.status} className="flex items-center justify-between border-b border-stone-100 pb-2">
                <span className={`border px-2 py-0.5 text-[11px] font-medium ${statusClass(s.status)}`}>
                  {statusLabel(s.status)}
                </span>
                <span className="font-heading text-lg font-bold">{s.count}</span>
              </div>
            ))}
          </div>
          <p className="mt-6 text-xs uppercase tracking-[0.2em] text-stone-500">Sipariş Veren Kurumlar</p>
          <div className="mt-3 space-y-2">
            {stats.topOrganisations.slice(0, 5).map((o) => (
              <div key={o.organisation} className="flex items-center justify-between text-sm">
                <span className="truncate text-stone-700">{o.organisation}</span>
                <span className="ml-2 shrink-0 text-xs text-stone-500">{o.orders} sipariş</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        <div className="fade-up border border-stone-200 bg-white" style={{ animationDelay: "360ms" }}>
          <p className="border-b border-stone-200 px-5 py-3 text-xs uppercase tracking-[0.2em] text-stone-500">
            Ürün Bazında Özet
          </p>
          <Table data-testid="top-products-table">
            <TableHeader>
              <TableRow>
                <TableHead className="text-xs">Ürün</TableHead>
                <TableHead className="text-right text-xs">Adet</TableHead>
                <TableHead className="text-right text-xs">Sipariş</TableHead>
                <TableHead className="text-right text-xs">Tutar</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {stats.topProducts.slice(0, 8).map((p, i) => (
                <TableRow key={`${p.sku || p.name}-${i}`} className="hover:bg-stone-50">
                  <TableCell className="py-2">
                    <p className="text-sm font-medium">{p.name}</p>
                    <p className="text-[11px] text-stone-400">{p.sku}</p>
                  </TableCell>
                  <TableCell className="py-2 text-right font-heading font-bold">{p.qty} <span className="text-xs font-medium text-stone-500">{unitLabel(p.unit)}</span></TableCell>
                  <TableCell className="py-2 text-right text-sm">{p.orders}</TableCell>
                  <TableCell className="py-2 text-right text-sm">{fmtMoney(p.total)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>

        <div className="fade-up border border-stone-200 bg-white" style={{ animationDelay: "420ms" }}>
          <p className="border-b border-stone-200 px-5 py-3 text-xs uppercase tracking-[0.2em] text-stone-500">
            Son Siparişler
          </p>
          <Table data-testid="recent-orders-table">
            <TableHeader>
              <TableRow>
                <TableHead className="text-xs">Referans</TableHead>
                <TableHead className="text-xs">Kurum</TableHead>
                <TableHead className="text-xs">Durum</TableHead>
                <TableHead className="text-right text-xs">Tutar</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {stats.recentOrders.map((o, i) => (
                <TableRow key={`${o.ref}-${i}`} className="hover:bg-stone-50">
                  <TableCell className="py-2">
                    <p className="text-xs font-medium">{o.ref}</p>
                    <p className="text-[11px] text-stone-400">{fmtDate(o.placedAt)}</p>
                  </TableCell>
                  <TableCell className="py-2 text-sm">{o.organisation}</TableCell>
                  <TableCell className="py-2">
                    <span className={`border px-2 py-0.5 text-[10px] font-medium ${statusClass(o.status)}`}>
                      {statusLabel(o.status)}
                    </span>
                  </TableCell>
                  <TableCell className="py-2 text-right text-sm">{fmtMoney(o.total, o.currency)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </div>
    </div>
  );
}
